'use es6';

import I18n from 'I18n';
import { getPropertyLabel, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { and, stringify } from '../../modify/filters/filter-parser';
import { addGroup, createDefaultCustomLogic, getParsedExpression, normalizeFiltering } from '../../modify/filters/filter-utils';
import { applySorting } from '../../modify/sorts/report-sort-utils';
import { DatePartTypes, ExpressionField, FieldSources, FieldTypes, UNDEFINED_BUCKET } from '../../schema/column-records';
import { Conditions, Filter, Filtering, Group } from '../../schema/filter-records';
import { ChannelNames, VisualTypes } from '../../schema/visual-records';
import { createDefaultDimension, createFieldFromFieldRef, getColumnLabel, getDatePartTransformValue, getDateTruncTransformValue, hasDatePart, hasDateTrunc, isFieldExpressionBased, shouldApplyFiscalYearOffset } from '../../utils/column-utils';
import { createDimensionTableReportForDrilldown, getReportXEncoding, getReportY1Encoding, getReportY2Encoding, EXPANDED_REPORT_COLUMNS_COUNT, tempRemoveEmptySnowflakeProperties } from '../../utils/report-utils';
import { getPrimaryDisplayLabelPropertyName } from '../../utils/table-utils';
import { getEncodingColumnAlias } from '../../utils/visual-utils';
import { reportHasDateComparisonFilter } from '../../utils/date-comparison-filter-utils';
import { List } from 'immutable';
import { toPropertyBasicType } from '../../../dataset/type-utils';
import { checkExpressionField, checkExpressionFieldType, expressionFieldsListToMap } from '../../utils/expression-field-utils';
import { DATASET_TABLE } from '../../schema/table-records';
import { dateTruncTypeToMomentUnitMap } from '../../utils/date-truncation-utils';
import { getFiscalYearBackwardsOffset } from 'reporting-data/lib/fiscalYearUtils';
import { applyEnableUndefinedDateBuckets } from '../../updaters/report/modifiers';
import { removeEncodingFromChannel } from '../../updaters/report';
import { NullLiteral } from 'reporting-snowflake/dataset/expression-records';
import { NullType, NumberType, UnionType } from '../../../dataset/type-records';
import { shouldHideProperty } from '../../utils/source-utils';
import { isColumnFlpRestricted, removeInvalidFiltersFromReport } from './drilldown-utils';
import { COLUMN_ROLES } from 'reporting-data/constants/relationalReports';
import { querifySelfAssociation } from '../../utils/self-association-utils';
import { DATE_PART_EXPRESSION_FIELD_ALIAS_PREFIX } from './constants';
import { getIsUngatedToSnowflakePropertyFilterMigration } from '../../utils/filter-utils';
import { getIsUngated } from '../../../shared/lib/utils';
import { MIGRATE_LEGACY_ILS_DATE_TIME_FILTERS } from 'reporting-data/constants/gates';
const DEFAULT_FILTER_ID = '0';
const offsetFiscalYear = (fiscalYearMonthName, moment) => {
  const offset = getFiscalYearBackwardsOffset(fiscalYearMonthName);
  return moment.subtract(offset, 'months');
};
const offsetFiscalYearUpperBound = (fiscalYearMonthName, moment) => {
  return offsetFiscalYear(fiscalYearMonthName, moment).endOf('month');
};
const offsetFiscalYearLowerBound = (fiscalYearMonthName, moment) => {
  return offsetFiscalYear(fiscalYearMonthName, moment).startOf('month');
};
const buildDatePartExpressionFieldAlias = column => `${DATE_PART_EXPRESSION_FIELD_ALIAS_PREFIX}${column.alias}`;
const buildDatePartExpressionFieldInput = (column, meta) => {
  const field = column.field;
  const datePartArgument = getDatePartTransformValue(column);
  const buildPropertyReferenceString = () => {
    const {
      datasetDefinition
    } = meta;
    if (datasetDefinition) {
      const datasetFieldLabel = datasetDefinition.fields.get(field.name).label;
      return `[${datasetFieldLabel}]`;
    }
    return `[${field.table}.${field.name}]`;
  };
  const propertyReferenceString = buildPropertyReferenceString();
  const useFiscalYear = column.get('useFiscalYear');
  const propertyArgumentString = useFiscalYear && [DatePartTypes.QUARTER, DatePartTypes.YEAR].includes(datePartArgument) ? `ADJUST_FISCAL_YEAR(${propertyReferenceString})` : propertyReferenceString;
  return `DATEPART(${datePartArgument},${propertyArgumentString})`;
};
const buildDatePartExpressionFromColumn = (column, expressions, meta) => {
  const input = buildDatePartExpressionFieldInput(column, meta);
  const expressionField = ExpressionField({
    alias: '__TEMP__',
    label: '__TEMP__',
    valid: true,
    expression: NullLiteral({}),
    input,
    context: 'ROW_LEVEL',
    type: UnionType({
      types: List([NumberType(), NullType()])
    }),
    hidden: true,
    derived: true,
    editor: undefined
  });
  const {
    field: {
      expression: nextExpression
    }
  } = checkExpressionField(expressionField, expressions, meta);
  return nextExpression;
};

/**
 * Note:
 * This function hard-codes the shape of filters (based on object segmentation filters).
 *
 * Reason:
 * Although `customer-data-filters` provides factory functions to standardize filter creation, using them introduces extra dependencies in `reporting-ui-components`.
 * Adding extra dependencies to downstream apps is not desirable at the moment (ie. bundle size, build complexity).
 *
 * Slack thread: https://hubspot.slack.com/archives/C5WR5PBFT/p1597421131273600
 */
const createFilterUsingColumn = (column, reportDefinition, reportMeta, value, accumulated, fiscalYearMonthName, userGates) => {
  const hasDateTruncTransform = hasDateTrunc(column);
  const hasDatePartTransform = hasDatePart(column);
  const isUngatedToTimepoints = getIsUngatedToSnowflakePropertyFilterMigration(userGates) && getIsUngated(MIGRATE_LEGACY_ILS_DATE_TIME_FILTERS, userGates);
  const field = column.field;
  const type = field.type;
  const propertyName = field.name;
  if (value == null || value === UNDEFINED_BUCKET) {
    const filter = {
      filterType: 'PROPERTY',
      operation: {
        propertyType: 'alltypes',
        operator: 'IS_UNKNOWN'
      },
      property: propertyName
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field,
      filter
    });
  }
  const isDateLikeField = type === FieldTypes.DATETIME || type === FieldTypes.DATE;
  const zoneId = I18n.moment.portalTz().tz() || 'UTC';

  // for now accumulated drilldowns only works on date-like fields
  if (accumulated && isDateLikeField && hasDateTruncTransform) {
    const dateTruncType = getDateTruncTransformValue(column);
    const momentUnit = dateTruncTypeToMomentUnitMap[dateTruncType];
    if (momentUnit == null) {
      throw new Error(`Unable to convert DATE_TRUNC type \`${dateTruncType}\` to a moment unit!`);
    }
    let upperBoundMoment = I18n.moment.utc(value).endOf(momentUnit);
    if (shouldApplyFiscalYearOffset(column)) {
      upperBoundMoment = offsetFiscalYearUpperBound(fiscalYearMonthName, upperBoundMoment);
    }
    const upperBoundTimestamp = upperBoundMoment.valueOf() + 1;
    const filter = isUngatedToTimepoints ? {
      filterType: 'PROPERTY',
      property: propertyName,
      operation: {
        propertyParser: 'VALUE',
        propertyType: 'rangedtime',
        operator: 'IS_BEFORE',
        timepoint: {
          timeType: 'DATE',
          year: upperBoundTimestamp.year(),
          // Timepoint months are 1-indexed, moment is 0-indexed
          month: 1 + upperBoundTimestamp.month(),
          day: upperBoundTimestamp.date(),
          hour: upperBoundTimestamp.hour(),
          minute: upperBoundTimestamp.minute(),
          second: upperBoundTimestamp.second(),
          zoneId
        },
        endpointBehavior: 'INCLUSIVE'
      }
    } : {
      filterType: 'PROPERTY',
      operation: {
        defaultValue: null,
        propertyType: 'datetime',
        operator: 'IS_BEFORE_DATE',
        timestamp: upperBoundTimestamp
      },
      property: propertyName
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field,
      filter
    });
  }
  if (accumulated && isDateLikeField && hasDatePartTransform) {
    const filter = {
      filterType: 'PROPERTY',
      operation: {
        propertyType: 'number',
        operator: 'IS_LESS_THAN_OR_EQUAL_TO',
        value: Number(value),
        includeObjectsWithNoValueSet: false,
        defaultValue: null
      },
      property: field.name
    };
    const expression = buildDatePartExpressionFromColumn(column, reportDefinition.expressions, reportMeta);
    const {
      type: expressionType
    } = checkExpressionFieldType(expression, reportDefinition.expressions, reportMeta);
    const expressionAlias = buildDatePartExpressionFieldAlias(column);
    filter.property = expressionAlias;
    const calculatedDatePartField = {
      name: expressionAlias,
      type: toPropertyBasicType(expressionType),
      source: FieldSources.EXPRESSION
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field: calculatedDatePartField,
      filter
    });
  }
  if (isDateLikeField && hasDatePartTransform) {
    const filter = {
      filterType: 'PROPERTY',
      operation: {
        propertyType: 'number',
        operator: 'IS_EQUAL_TO',
        value: Number(value),
        includeObjectsWithNoValueSet: false,
        defaultValue: null
      },
      property: field.name
    };
    const expression = buildDatePartExpressionFromColumn(column, reportDefinition.expressions, reportMeta);
    const {
      type: expressionType
    } = checkExpressionFieldType(expression, reportDefinition.expressions, reportMeta);
    const expressionAlias = buildDatePartExpressionFieldAlias(column);
    filter.property = expressionAlias;
    const calculatedDatePartField = {
      name: expressionAlias,
      type: toPropertyBasicType(expressionType),
      source: FieldSources.EXPRESSION
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field: calculatedDatePartField,
      filter
    });
  }
  if (isDateLikeField && hasDateTruncTransform) {
    const dateTruncType = getDateTruncTransformValue(column);
    const momentUnit = dateTruncTypeToMomentUnitMap[dateTruncType];
    if (momentUnit == null) {
      throw new Error(`Unable to convert DATE_TRUNC type \`${dateTruncType}\` to a moment unit!`);
    }
    let lowerBoundMoment = I18n.moment.utc(value).startOf(momentUnit);
    let upperBoundMoment = I18n.moment.utc(value).endOf(momentUnit);
    if (typeof value === 'string' && !isNaN(value)) {
      lowerBoundMoment = I18n.moment.utc(Number(value)).startOf(momentUnit);
      upperBoundMoment = I18n.moment.utc(Number(value)).endOf(momentUnit);
    }
    if (shouldApplyFiscalYearOffset(column)) {
      lowerBoundMoment = offsetFiscalYearLowerBound(fiscalYearMonthName, lowerBoundMoment);
      upperBoundMoment = offsetFiscalYearUpperBound(fiscalYearMonthName, upperBoundMoment);
    }
    const lowerBoundTimestamp = lowerBoundMoment.valueOf();
    const upperBoundTimestamp = upperBoundMoment.valueOf();
    const filter = isUngatedToTimepoints ? {
      filterType: 'PROPERTY',
      operation: {
        propertyParser: 'VALUE',
        propertyType: 'rangedtime',
        operator: 'IS_BETWEEN',
        lowerBoundTimePoint: {
          timeType: 'DATE',
          year: lowerBoundMoment.year(),
          // Timepoint months are 1-indexed, moment is 0-indexed
          month: 1 + lowerBoundMoment.month(),
          day: lowerBoundMoment.date(),
          hour: lowerBoundMoment.hour(),
          minute: lowerBoundMoment.minute(),
          second: lowerBoundMoment.second(),
          zoneId
        },
        upperBoundTimePoint: {
          timeType: 'DATE',
          year: upperBoundMoment.year(),
          // Timepoint months are 1-indexed, moment is 0-indexed
          month: 1 + upperBoundMoment.month(),
          day: upperBoundMoment.date(),
          hour: upperBoundMoment.hour(),
          minute: upperBoundMoment.minute(),
          second: upperBoundMoment.second(),
          zoneId
        },
        lowerBoundEndpointBehavior: 'INCLUSIVE',
        upperBoundEndpointBehavior: 'INCLUSIVE'
      },
      property: propertyName
    } : {
      filterType: 'PROPERTY',
      operation: {
        defaultValue: null,
        propertyType: 'datetime-ranged',
        operator: 'IS_BETWEEN',
        lowerBoundTimestamp,
        upperBoundTimestamp
      },
      property: propertyName
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field,
      filter
    });
  }
  if (isDateLikeField) {
    const maybeTimestamp = Number(value);
    const timestamp = isNaN(maybeTimestamp) ? I18n.moment.utc(value, 'YYYY-MM-DD').valueOf() : maybeTimestamp;
    const filter = {
      filterType: 'PROPERTY',
      operation: {
        defaultValue: null,
        propertyType: 'datetime',
        operator: 'IS_EQUAL_TO',
        timestamp
      },
      property: propertyName
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field,
      filter
    });
  }
  if (type === FieldTypes.STRING) {
    const filter = {
      filterType: 'PROPERTY',
      operation: {
        defaultValue: null,
        propertyType: 'multistring',
        operator: 'IS_EQUAL_TO',
        values: [value]
      },
      property: propertyName
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field,
      filter
    });
  }
  if (type === FieldTypes.NUMBER) {
    const filter = {
      filterType: 'PROPERTY',
      operation: {
        defaultValue: null,
        propertyType: 'number',
        operator: 'IS_EQUAL_TO',
        value
      },
      property: propertyName
    };
    return Filter({
      id: DEFAULT_FILTER_ID,
      field,
      filter
    });
  }
  const filter = {
    filterType: 'PROPERTY',
    operation: {
      defaultValue: null,
      propertyType: 'enumeration',
      operator: 'IS_ANY_OF',
      values: [value]
    },
    property: propertyName
  };
  return Filter({
    id: DEFAULT_FILTER_ID,
    field,
    filter
  });
};
const convertToCustomLogic = filtering => {
  if (filtering.condition === Conditions.CUSTOM) {
    return filtering;
  }
  const logic = createDefaultCustomLogic(filtering);
  return filtering.set('logic', logic).set('condition', Conditions.CUSTOM);
};
const andGroupId = (filtering, groupId) => {
  const oldLogic = filtering.logic;
  const allDataLogic = oldLogic === '';
  const computeLogic = () => {
    if (allDataLogic) {
      return groupId;
    }
    try {
      const expression = getParsedExpression(filtering);
      return stringify(and(expression, groupId));
    } catch (e) {
      // todo log this error to new relic / sentry
      console.error(e);
      return oldLogic;
    }
  };
  const newLogic = computeLogic();
  return filtering.set('logic', newLogic);
};
const defaultFiltering = Filtering({
  condition: Conditions.AND,
  groups: [],
  logic: undefined,
  stagedFilters: []
});
const applyDrilldownFiltering = (filtering = defaultFiltering, drilldownPointFilters, dateComparisonFilters) => {
  // Normalize filter IDs first to ensure they are sequential and match logic expression
  let next = normalizeFiltering(filtering);
  next = convertToCustomLogic(next);
  if (!drilldownPointFilters.isEmpty()) {
    const newGroupId = String(filtering.groups.count() + 1);
    const newGroup = Group({
      id: newGroupId,
      condition: Conditions.AND,
      filters: drilldownPointFilters.toList()
    });
    next = addGroup(next, newGroup);
    next = andGroupId(next, newGroupId);
  }
  if (!dateComparisonFilters.isEmpty()) {
    const newGroupId = String(next.groups.count() + 1);
    const newGroup = Group({
      id: newGroupId,
      condition: Conditions.OR,
      filters: dateComparisonFilters
    });
    next = addGroup(next, newGroup);
    next = andGroupId(next, newGroupId);
  }
  return next;
};

/**
 * @returns {string?} columnId of the column the accumulation is running on, if there is any accumulation enabled
 */
const guessAccumulatedAlongColumn = (reportDefinition, dataRow) => {
  const visual = reportDefinition.visual;
  if (visual.type === VisualTypes.LINE || visual.type === VisualTypes.AREA) {
    if (!visual.options.accumulate) {
      return undefined;
    }
    const x = getReportXEncoding(reportDefinition);
    return x && getEncodingColumnAlias(x);
  } else if (visual.type === VisualTypes.COMBO) {
    if (!visual.options.y1Accumulate && !visual.options.y2Accumulate) {
      return undefined;
    }
    const x = getReportXEncoding(reportDefinition);
    const xId = x && getEncodingColumnAlias(x);

    // checks if the data row belongs to an accumulated measure axis
    if (visual.options.y1Accumulate) {
      const y1 = getReportY1Encoding(reportDefinition);
      const y1Id = y1 && getEncodingColumnAlias(y1);
      return y1Id && dataRow.has(y1Id) ? xId : undefined;
    }
    if (visual.options.y2Accumulate) {
      const y2 = getReportY2Encoding(reportDefinition);
      const y2Id = y2 && getEncodingColumnAlias(y2);
      return y2Id && dataRow.has(y2Id) ? xId : undefined;
    }
    return undefined;
  } else {
    return undefined;
  }
};
export const createDrilldownFilters = (dataRow, reportMeta, reportDefinition, fiscalYearMonthName, userGates) => {
  const mapOfDimensionColumnToValue = reportDefinition.columns.filter(column => column.role === COLUMN_ROLES.DIMENSION).filter(column => dataRow.has(column.alias)).flip().map(columnId => dataRow.get(columnId));
  const accumulateAlongColumnId = guessAccumulatedAlongColumn(reportDefinition, dataRow);
  return mapOfDimensionColumnToValue.map((value, column) => createFilterUsingColumn(column, reportDefinition, reportMeta, value, accumulateAlongColumnId === column.alias, fiscalYearMonthName, userGates));
};
const getDimensionFiltering = (dataRow, reportMeta, reportDefinition, fiscalYearMonthName, userGates) => {
  const drilldownFilters = createDrilldownFilters(dataRow, reportMeta, reportDefinition, fiscalYearMonthName, userGates);
  const nextReportDefinition = querifySelfAssociation(reportDefinition);
  return applyDrilldownFiltering(nextReportDefinition.filtering, drilldownFilters, reportHasDateComparisonFilter(reportDefinition) ? reportDefinition.dateComparisonFilter.filters : List());
};
export const stageDisplayLabelFields = (reportMeta, reportDefinition) => {
  const setOfDisplayFieldRefs = getPrimaryDisplayLabelPropertyName(reportMeta.tables, reportDefinition.table).map(fieldRef => createFieldFromFieldRef(fieldRef, reportMeta.properties.getIn([fieldRef.table, fieldRef.property]))).map(createDefaultDimension);
  return reportDefinition.update('stagedColumns', stagedColumns => stagedColumns.concat(setOfDisplayFieldRefs.toList()));
};
export const createDrilledExpressionFields = (reportDefinition, meta) => {
  const datePartDimensions = reportDefinition.columns.toList().filter(column => {
    const hasDatePartTransform = hasDatePart(column);
    const type = column.field.type;
    const isDateLikeField = type === FieldTypes.DATETIME || type === FieldTypes.DATE;
    return isDateLikeField && hasDatePartTransform;
  });
  const datePartExpressions = datePartDimensions.map(column => {
    const input = buildDatePartExpressionFieldInput(column, meta);
    const expression = buildDatePartExpressionFromColumn(column, reportDefinition.expressions, meta);
    const {
      field
    } = column;
    const {
      type: expressionType
    } = checkExpressionFieldType(expression, reportDefinition.expressions, meta);
    const {
      tables,
      properties,
      datasetDefinition
    } = meta;
    const snowflakeProperty = properties && properties.getIn([field.table, field.name]);
    const snowflakeTable = tables && tables.get(field.table);
    const maybeDatasetField = field.table === DATASET_TABLE && datasetDefinition && datasetDefinition.getIn(['fields', field.name]);
    const columnName = getColumnLabel(column, snowflakeProperty, snowflakeTable, maybeDatasetField, reportDefinition.expressions);
    return ExpressionField({
      alias: buildDatePartExpressionFieldAlias(column),
      label: columnName,
      expression,
      input,
      type: expressionType,
      derived: true,
      hidden: false,
      valid: true
    });
  });
  return reportDefinition.expressions.concat(datePartExpressions);
};
const trimToMaximumReportColumns = (reportDefinition, deps) => {
  if (!reportDefinition.visual.type === VisualTypes.TABLE) {
    return reportDefinition;
  }
  const excessColumnEncodings = reportDefinition.visual.encodings[ChannelNames.COLUMNS].skip(EXPANDED_REPORT_COLUMNS_COUNT);
  return excessColumnEncodings.reduce(acc => {
    return removeEncodingFromChannel(acc, {
      deps,
      channelName: ChannelNames.COLUMNS,
      index: EXPANDED_REPORT_COLUMNS_COUNT
    });
  }, reportDefinition);
};
const removeInvalidFieldsFromReport = (reportDefinition, meta) => {
  const stagedColumns = reportDefinition.stagedColumns;
  const expressionFieldsList = reportDefinition.expressions;
  const isColumnValid = column => {
    if (isFieldExpressionBased(column.field)) {
      const expressionFieldToCheck = expressionFieldsList.find(expressionField => expressionField.alias === column.field.name);
      const {
        violations
      } = checkExpressionField(expressionFieldToCheck, expressionFieldsList, meta);
      return violations.isEmpty();
    } else if (meta.datasetDefinition) {
      return meta.datasetDefinition.fields.has(column.field.name);
    } else if (column.field.source === FieldSources.TABLE) {
      const {
        field: {
          table,
          name
        }
      } = column;
      const propertyTable = meta.properties.get(table);
      const property = propertyTable && propertyTable.get(name);
      if (!property) {
        return false;
      }
      return !shouldHideProperty(property);
    }
    return true;
  };
  const restrictedColumns = stagedColumns.filter(column => isColumnFlpRestricted(reportDefinition, meta, column));
  const invalidColumns = stagedColumns.filterNot(isColumnValid);
  const invalidOrRestrictedColumns = restrictedColumns.concat(invalidColumns);
  const invalidOrRestrictedColumnAliases = invalidOrRestrictedColumns.map(column => column.alias);
  const invalidOrRestrictedExpressionAliases = invalidOrRestrictedColumns.filter(column => isFieldExpressionBased(column.field)).map(column => column.field.name);
  return reportDefinition.update('stagedColumns', columns => columns.filterNot(column => invalidOrRestrictedColumnAliases.includes(column.alias))).update('stagedEncodings', encodings => encodings.filterNot(encoding => invalidOrRestrictedColumnAliases.includes(encoding.column))).update('expressions', expressions => expressions.filterNot(expression => invalidOrRestrictedExpressionAliases.includes(expression.alias)));
};
export const generateDrilledDefinition = (dataRow, deps,
// todo revert this if we don't need all the deps
reportDefinition, fiscalYearMonthName, userGates) => {
  const {
    meta: reportMeta
  } = deps;
  reportDefinition = removeInvalidFieldsFromReport(reportDefinition, reportMeta);
  reportDefinition = removeInvalidFiltersFromReport(reportDefinition);
  const dimensionFiltering = getDimensionFiltering(dataRow, reportMeta, reportDefinition, fiscalYearMonthName, userGates);
  const expressionFields = createDrilledExpressionFields(reportDefinition, reportMeta);
  const expressionsMap = expressionFieldsListToMap(expressionFields);
  let next = tempRemoveEmptySnowflakeProperties(reportDefinition, reportMeta);
  next = stageDisplayLabelFields(reportMeta, next);
  next = applyEnableUndefinedDateBuckets(createDimensionTableReportForDrilldown(next, reportMeta));
  next = next.update('columns', columns => columns.map(column => isFieldExpressionBased(column.field) ? column.set('name', expressionsMap.get(column.field.name).label) : column));
  next = next.set('filtering', dimensionFiltering);
  next = applySorting(next, reportMeta);
  next = next.set('expressions', expressionFields);
  next = trimToMaximumReportColumns(next, deps);
  return next;
};
export const generateDrilldownName = (clickEvent, dataset, reportDefinition) => {
  const {
    row,
    field,
    levelOfDetail
  } = clickEvent;
  const labelColumn = column => {
    const {
      alias: id,
      role
    } = column;
    if (role === COLUMN_ROLES.DIMENSION) {
      if (levelOfDetail && !levelOfDetail.includes(id)) {
        return null;
      }
      return getReferenceLabel(dataset, id, row.get(id));
    } else if (role === COLUMN_ROLES.MEASURE && id === field) {
      return getPropertyLabel(dataset, id);
    } else {
      return undefined;
    }
  };
  return reportDefinition.columns.toList().sort(column => column.role === COLUMN_ROLES.DIMENSION ? -1 : 1).map(labelColumn).filter(label => label != null).join(', ');
};